import NiceModal from '@ebay/nice-modal-react';

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Button from 'components/field/Button';
import SearchInput from 'components/field/SearchInput';
import SwitchInput from 'components/field/SwitchInput';
import { NonFormContainer } from 'components/form/FormLayout.styled';
import ConfirmationModal from 'components/global/ConfirmationModal';
import Image from 'components/global/Image';
import LottiePlayer from 'components/global/LottiePlayer';
import Skeleton from 'components/global/Skeleton';
import SubformHeader from 'components/global/SubformHeader';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useDebounce from 'hooks/useDebounce';
import useMediaQuery from 'hooks/useMediaQuery';

import { OTHER_INTEGRATIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';
import PATHS, { mergePaths } from 'utils/paths';

import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { faCubes, faEdit } from '@fortawesome/pro-solid-svg-icons';

import IntegrationDynamicForm from '../Integrations/Modals/IntegrationDynamicForm';
import integrationModalNames from '../Integrations/integrationModalNames';
import * as S from './ConnectedIntegrations.styled';
import { setConnectedIntegrations } from './ConnectedIntegrationsSlice';

/**
 *
 * @author Burak Ak
 */

const Connected = () => {
	const { showError } = useAlert();
	const [isLoading, setIsLoading] = useState(true);
	const axiosPrivate = useAxiosPrivate();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [filteredList, setFilteredList] = useState([]);
	const connectedIntegrationList = useSelector((state) => state?.connectedIntegrations);

	const [searchValue, setSearchValue] = useState(null);
	const debouncedValue = useDebounce(searchValue, 500);

	const getIntegrations = async () => {
		setIsLoading(true);

		try {
			const response = await axiosPrivate.post(OTHER_INTEGRATIONS.GET_CONNNECTED_INTEGRATIONS);

			if (response.status === 200) {
				dispatch(setConnectedIntegrations(response.data?.reverse()));
				setIsLoading(false);
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
		setIsLoading(false);
	};

	useEffect(() => {
		getIntegrations();
	}, []);

	useEffect(() => {
		if (debouncedValue) {
			setFilteredList(
				connectedIntegrationList?.filter(
					(item) =>
						item.title?.toLowerCase().includes(debouncedValue.toLowerCase()) ||
						item.title?.includes(debouncedValue.toLowerCase()) ||
						item.title?.toLowerCase().split(' ').join('').includes(debouncedValue.toLowerCase()) ||
						item.configName.toLowerCase().includes(debouncedValue.toLowerCase()) ||
						item.configName.includes(debouncedValue.toLowerCase()) ||
						item.configName.toLowerCase().split(' ').join('').includes(debouncedValue.toLowerCase())
				)
			);
		} else {
			setFilteredList(connectedIntegrationList);
		}
	}, [debouncedValue, connectedIntegrationList]);

	return (
		<>
			<NonFormContainer>
				<SubformHeader
					lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/otherIntegrations-icon.json.gz"
					trigger="hover"
					className="table-lord-icon"
					icon={faCubes}
					boldTitle="connectedIntegrations"
					lightTitle="connectedIntegrationsSubtitle"
				/>
				<S.InputContainer>
					<SearchInput
						setValue={setSearchValue}
						value={searchValue}
						variant="grey"
						placeholder="searchKeyword"
						fullWidth
					/>
				</S.InputContainer>

				{isLoading ? (
					<S.PartnersContainer>
						{[...Array(5)].map((_, i) => (
							<S.LoadingRowSpaced key={i}>
								<S.LoadingRowCentered>
									<div className="p-3">
										<Skeleton width="6rem" height="6rem" />
									</div>

									<div>
										<Skeleton className="loadingHeaderPadding" width="7rem" height="1rem" />
										<Skeleton className="loadingHeaderPadding" width="9rem" height="1rem" />
									</div>
								</S.LoadingRowCentered>

								<S.LoadingRowCenteredActions>
									<Skeleton width="4rem" height="1.5rem" />
									<Skeleton width="7.5rem" height="3rem" />
									<Skeleton width="1.7rem" height="2.5rem" />
								</S.LoadingRowCenteredActions>
							</S.LoadingRowSpaced>
						))}
					</S.PartnersContainer>
				) : (
					<>
						{filteredList?.length ? (
							<S.PartnersContainer>
								{filteredList?.map((data) => (
									<IntegrationBox key={data.smsConfigID} {...data} />
								))}
							</S.PartnersContainer>
						) : (
							<S.PartnersContainer className="mt-5">
								<LottiePlayer name="other-integrations-empty" width="auto" height="300px" />
								<div style={{ textAlign: 'center' }}>
									<Typography className="emptySubtitle" noWrap>
										emptyOtherIntegrationsSubtitle
									</Typography>
									<Button
										style={{ marginLeft: 'Auto', marginRight: 'Auto' }}
										icon={faCubes}
										label="connectNow"
										className="mb-3"
										onClick={() =>
											navigate(mergePaths(PATHS.OTHER_INTEGRATIONS, PATHS.INTEGRATIONS))
										}
									/>
								</div>
							</S.PartnersContainer>
						)}
					</>
				)}
			</NonFormContainer>
		</>
	);
};

const IntegrationBox = ({ ID, title, configName, status, smsConfigID, type, logo, keyString }) => {
	const { showSuccess, showError } = useAlert();
	const connectedIntegrationsList = useSelector((state) => state?.connectedIntegrations);

	const dispatch = useDispatch();
	const axiosPrivate = useAxiosPrivate();

	const statusChange = async (smsConfigID, status) => {
		const requestActive = !status;

		const URL = type === 'sms' || keyString === 'whatsappBusiness' ? OTHER_INTEGRATIONS.UPDATE_SMS_STATUS : null;
		try {
			const response = await axiosPrivate.post(URL, {
				ID: smsConfigID,
				status: requestActive
			});
			if (response.status === 200) {
				changeLocalStatus(smsConfigID, requestActive);
				showSuccess();
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	const changeLocalStatus = (smsConfigID, requestActive) => {
		const connectedIntegrationsListDummy = JSON.parse(JSON.stringify(connectedIntegrationsList));
		const index = connectedIntegrationsListDummy.findIndex((item) => item.smsConfigID === smsConfigID);
		connectedIntegrationsListDummy[index].status = requestActive;
		dispatch(setConnectedIntegrations(connectedIntegrationsListDummy));
	};

	const deleteRow = async (smsConfigID) => {
		const URL = type === 'sms' || keyString === 'whatsappBusiness' ? OTHER_INTEGRATIONS.DELETE_SMS_PROVIDER : null;
		try {
			const response = await axiosPrivate.post(URL, {
				ID: smsConfigID
			});
			if (response.status === 200) {
				showSuccess();

				dispatch(
					setConnectedIntegrations(
						connectedIntegrationsList.filter((item) => item.smsConfigID !== smsConfigID)
					)
				);
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			if (error.response?.data.code === 'error.C017') {
				showError({
					header: 'errorMsg.thisProviderIsConnected',
					message: 'errorMsg.thisProviderIsConnectedMessage'
				});
			} else {
				showError();
				AppLogger.error(error);
			}
		}
	};
	const isMobile = useMediaQuery(Breakpoints.mobileLandscape);

	const handleOpenModal = (e, form) => {
		e.stopPropagation();
		if (form) {
			e.preventDefault();
			NiceModal.show(form, {
				title: 'areYouSureYouWantToDeleteThis',
				subTitle: 'thisWillDeleteTheSelectedItemPermanently',
				rejectLabel: 'no',
				confirmLabel: 'yes',
				yesAction: () => deleteRow(smsConfigID)
			});
		}
	};

	return (
		<S.RowSpaced>
			<S.RowCentered>
				<div className="p-3">
					<Image url={logo} height="6rem" width="6rem" objectFit="scale-down" radius={4} />
				</div>
				<div style={{ width: isMobile ? '10rem' : '150px' }}>
					<Typography noWrap={true} variant={isMobile ? 'p3' : 'p1'} fontWeight="bold">
						{title}
					</Typography>
					<Typography noWrap={true}>{configName}</Typography>
				</div>
			</S.RowCentered>

			<S.RowCenteredActions>
				{status !== undefined && (
					<SwitchInput
						style={{ margin: '0 1rem' }}
						value={status}
						setValue={() => {
							statusChange(smsConfigID, status);
						}}
					/>
				)}

				<Button
					variant="blackOutline"
					icon={faEdit}
					label={isMobile ? '' : 'edit'}
					style={{ margin: isMobile ? 0 : '0 1rem' }}
					onClick={() => {
						const modal = integrationModalNames.find((item) => item.keyString === keyString);
						if (!modal) {
							NiceModal.show(IntegrationDynamicForm, {
								smsConfigID,
								logo,
								title
							});
						} else {
							NiceModal.show(modal.component, {
								smsConfigID,
								logo,
								title,
								ID,
								mode: 'edit'
							});
						}
					}}
					size="small"
				/>

				{type !== 'webhooks' ? (
					<Button
						className="deleteButton"
						icon={faTrashCan}
						variant="transparent"
						color="var(--red-70)"
						size="large"
						iconSize="medium"
						iconButton
						onClick={(e) => handleOpenModal(e, ConfirmationModal)}
					/>
				) : (
					<div style={{ width: '5rem' }} />
				)}
			</S.RowCenteredActions>
		</S.RowSpaced>
	);
};

export default Connected;
